import { Box, BoxProps, Typography } from "@mui/material";
import { ReactNode } from "react";

interface GradientRewardBoxProps extends Omit<BoxProps, "children"> {
  children: ReactNode;
  programColor?: string;
  enableHover?: boolean;
}

export const GradientRewardBox = ({
  children,
  programColor,
  enableHover = true,
  sx,
  ...props
}: GradientRewardBoxProps) => {
  // Safe fallback for programColor to prevent undefined in CSS
  const safeProgramColor = programColor || "#E0E0E0";

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        position: "relative",
        background: `linear-gradient(135deg, ${safeProgramColor} 0%, rgb(255, 255, 255, .2) 100%)`,
        borderTop: "1px solid rgba(255, 255, 255, 0.5)",
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: `
          inset 0 1px 0 0 rgba(255, 255, 255, 0.8),
          0 2px 4px -1px rgba(0, 0, 0, 0.06),
          0 4px 8px -2px rgba(0, 0, 0, 0.04)
        `,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        ...(enableHover && {
          "&:hover": {
            transform: "translateX(2px)",
            boxShadow: `
              inset 0 1px 0 0 rgba(255, 255, 255, 0.9),
              0 4px 8px -2px rgba(0, 0, 0, 0.08),
              0 8px 16px -4px rgba(0, 0, 0, 0.06),
              inset -2px 0 0 0 ${safeProgramColor}
            `,
          },
        }),
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: `linear-gradient(90deg, ${safeProgramColor} 0%, rgba(255, 255, 255, 0) 50%)`,
          opacity: 0.6,
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

interface RewardTextProps {
  title: string;
  description?: string;
  amount?: number;
  titleColor?: string;
}

export const RewardText = ({
  title,
  description,
  amount,
  titleColor = "text.primary",
}: RewardTextProps) => {
  return (
    <>
      <Typography
        variant="subtitle1"
        sx={{
          mb: 1,
          fontWeight: 700,
          fontSize: { xs: "1.05rem", sm: "1.15rem" },
          lineHeight: 1.3,
          letterSpacing: "-0.01em",
          textShadow: "0 1px 2px rgba(255, 255, 255, 0.8)",
          color: titleColor,
          pr: 6,
        }}
      >
        {title}
      </Typography>
      {amount !== undefined && (
        <Typography
          variant="h6"
          sx={{
            color: "success.main",
            fontWeight: 700,
            mb: description ? 0.5 : 0,
          }}
        >
          {amount} pts
        </Typography>
      )}
      {description && (
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: { xs: 13, sm: 15 },
            lineHeight: 1.6,
            fontWeight: 400,
          }}
        >
          {description}
        </Typography>
      )}
    </>
  );
};
